<template>
  <v-container fluid class="pa-6">
    <!-- Header Section -->
    <div class="d-flex align-center justify-space-between mb-6">
      <div>
        <h1 class="text-h5 font-weight-bold mb-1">Proteins Management</h1>
        <p class="text-body-2 text-medium-emphasis mb-0">Manage proteins available for menus</p>
      </div>
      <v-btn color="primary" prepend-icon="mdi-plus" @click="openModal(null)">
        Add Protein
      </v-btn>
    </div>

    <!-- Proteins Table -->
    <v-card rounded="lg" border>
      <v-data-table-server
        :headers="headers"
        :items="proteins"
        :items-length="totalItems"
        :items-per-page="itemsPerPage"
        :page="page"
        :items-per-page-options="[20, 50, 100]"
        :loading="loading"
        class="elevation-0"
        @update:options="onTableOptionsUpdate"
      >
        <template v-slot:item.type="{ item }">
          <v-chip
            :color="item.type === 'premium' ? 'amber-darken-2' : 'blue-grey'"
            size="small"
            label
          >
            {{ item.type.charAt(0).toUpperCase() + item.type.slice(1) }}
          </v-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn icon variant="text" size="small" color="primary" @click="openModal(item)">
            <v-icon size="small">mdi-pencil</v-icon>
          </v-btn>
          <v-btn icon variant="text" size="small" color="error" @click="deleteProtein(item.id)">
            <v-icon size="small">mdi-delete</v-icon>
          </v-btn>
        </template>
      </v-data-table-server>
    </v-card>

    <!-- Add/Edit Dialog -->
    <v-dialog v-model="isModalOpen" max-width="500px">
      <v-card class="pa-2">
        <v-card-title class="text-h6 d-flex align-center">
          <v-icon color="primary" class="mr-2">{{ selectedProtein ? 'mdi-pencil' : 'mdi-plus-circle' }}</v-icon>
          {{ selectedProtein ? "Edit Protein" : "Add Protein" }}
        </v-card-title>
        <v-card-text>
          <v-text-field
            label="Name"
            v-model="form.name"
            variant="outlined"
            density="comfortable"
            class="mb-3"
            required
          ></v-text-field>
          <v-select
            label="Type"
            v-model="form.type"
            :items="proteinTypes"
            item-title="label"
            item-value="value"
            variant="outlined"
            density="comfortable"
            required
          ></v-select>
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="closeModal">Cancel</v-btn>
          <v-btn color="primary" variant="flat" @click="saveProtein">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, reactive } from "vue";
import { proteinService } from "@/services/proteinService";
import { useNotificationStore } from "@/stores";

const notificationStore = useNotificationStore();

const proteins = ref([]);
const totalItems = ref(0);
const page = ref(1);
const itemsPerPage = ref(20);
const loading = ref(false);
const isModalOpen = ref(false);
const selectedProtein = ref(null);

const headers = [
  { title: "Name", key: "name" },
  { title: "Type", key: "type" },
  { title: "Actions", key: "actions", sortable: false, align: "center" },
];

const proteinTypes = [
  { label: "Classic", value: "classic" },
  { label: "Premium", value: "premium" },
];

const form = reactive({
  name: "",
  type: "classic",
});

async function fetchProteins() {
  loading.value = true;
  try {
    const data = await proteinService.getProteins({ page: page.value, limit: itemsPerPage.value });
    proteins.value = data.data || [];
    totalItems.value = data.total || 0;
  } catch (error) {
    notificationStore.handleApiError(error, "Failed to load proteins");
  } finally {
    loading.value = false;
  }
}

function onTableOptionsUpdate(options) {
  page.value = options.page;
  itemsPerPage.value = options.itemsPerPage;
  fetchProteins();
}

function openModal(protein) {
  selectedProtein.value = protein;
  if (protein) {
    Object.assign(form, {
      name: protein.name,
      type: protein.type,
    });
  } else {
    Object.assign(form, {
      name: "",
      type: "classic",
    });
  }
  isModalOpen.value = true;
}

function closeModal() {
  isModalOpen.value = false;
}

async function saveProtein() {
  try {
    if (selectedProtein.value) {
      await proteinService.updateProtein(selectedProtein.value.id, form);
      notificationStore.showSuccess("Protein updated successfully");
    } else {
      await proteinService.createProtein(form);
      notificationStore.showSuccess("Protein created successfully");
    }
    fetchProteins();
    closeModal();
  } catch (error) {
    notificationStore.handleApiError(error, "Failed to save protein");
  }
}

async function deleteProtein(id) {
  if (confirm("Are you sure you want to delete this protein?")) {
    try {
      await proteinService.deleteProtein(id);
      notificationStore.showSuccess("Protein deleted successfully");
      fetchProteins();
    } catch (error) {
      notificationStore.handleApiError(error, "Failed to delete protein");
    }
  }
}

</script>
